import { CircularProgress, Stack, Typography } from '@mui/material';
import {
  GridActionsCellItem,
  type GridActionsColDef,
  type GridColDef,
} from '@mui/x-data-grid-premium';
import { Download, Eye, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { type FileFragment } from '@graphql/__generated__/graphql';
import { FileIcon } from '@modules/_shared/components/FileIcon';
import { useLocale } from '@shared/hooks/locale';

type FileDefRow = { file: FileFragment } & { __isPending?: boolean };

export const useFileDef = <T extends FileDefRow>({
  disableLink = false,
}: { disableLink?: boolean } = {}) => {
  const { t } = useLocale();
  const fileDef: GridColDef<T> = {
    field: 'file',
    headerName: t('File'),
    display: 'flex',
    flex: 2,
    cellClassName: 'file-cell',
    minWidth: 150,
    renderCell: ({ row: { file, __isPending } }) => {
      const hasViewer = file.mimetype === 'application/pdf';

      return (
        <Stack
          direction="row"
          spacing={1}
          sx={{ width: '100%', alignItems: 'center' }}
        >
          {__isPending && (
            <CircularProgress size={20} disableShrink sx={{ flexShrink: 0 }} />
          )}
          <FileIcon
            mimetype={file.mimetype ?? ''}
            filename={file.name}
            size="medium"
          />
          <Typography noWrap sx={{ minWidth: 0, fontSize: '0.875rem' }}>
            {hasViewer || __isPending || disableLink ? (
              file.name
            ) : (
              <a href={file.url} target="_blank" rel="noopener noreferrer">
                {file.name}
              </a>
            )}
          </Typography>
        </Stack>
      );
    },
    sortable: false,
  };
  return fileDef;
};

export const useUploaderDef = <T extends FileDefRow>() => {
  const { t } = useLocale();
  const uploaderDef: GridColDef<T> = {
    field: 'uploader',
    flex: 1,
    minWidth: 150,
    headerName: t('Uploaded by'),
    renderCell: ({ row: { file } }) =>
      file.uploader != null ? (
        <Link to={`/users/${file.uploader.id}`}>
          {[file.uploader.first_name, file.uploader.last_name]
            .filter(Boolean)
            .join(' ')}
        </Link>
      ) : null,
    sortable: false,
  };
  return uploaderDef;
};

export const useFileActionsDef = <T extends FileDefRow>({
  setViewerFile,
  deleteFile,
  setDeleteFile,
  props,
  showInMenu = false,
}: {
  setViewerFile: (file: FileFragment) => void;
  deleteFile: T | null;
  setDeleteFile: (file: T) => void;
  props?: Partial<GridActionsColDef<T>>;
  showInMenu?: boolean;
}) => {
  const { t } = useLocale();
  const fileActionsDef: GridActionsColDef<T> = {
    field: 'actions',
    type: 'actions',
    align: 'right',
    getActions: ({ row }) => {
      if (row.__isPending) {
        return [];
      }

      const hasViewer = row.file.mimetype === 'application/pdf';
      const actionsMap = [
        {
          key: 'preview',
          icon: <Eye size={18} />,
          label: t('Preview'),
          onClick: () => setViewerFile(row.file),
        },
        {
          key: 'download',
          icon: <Download size={18} />,
          label: t('Download'),
          onClick: () => {
            const anchor = document.createElement('a');
            anchor.href = row.file.url ?? '';
            anchor.download = row.file.name ?? '';
            anchor.target = '_blank';
            anchor.rel = 'noopener noreferrer';
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
          },
        },
        {
          key: 'delete',
          icon: <Trash2 size={18} />,
          label: t('Delete'),
          disabled: deleteFile != null,
          onClick: () => setDeleteFile(row),
        },
      ].filter(action => hasViewer || action.key !== 'preview');

      return actionsMap.map(action => (
        <GridActionsCellItem
          key={action.key}
          icon={action.icon}
          label={action.label}
          onClick={action.onClick}
          color="inherit"
          showInMenu={showInMenu}
        />
      ));
    },
    ...props,
  };
  return fileActionsDef;
};
